import { makeAutoObservable } from 'mobx';
import { api } from 'utils/api/api';
import {
  AttRecordCreateByCodeRequestT,
  AttRecordOfTeacherCreateByCodeRequestT,
} from 'types/shared/reqAndRes/Attendance';
import { SimpleStudentT } from 'types/shared/core/Student';
import { MyResponse } from 'types/shared/reqAndRes/MyResponse';
import {
  CREATE_ATT_RECORD_FROM_DEVICE,
  CREATE_ATT_RECORD_OF_TEACHER_FROM_DEVICE,
} from 'network/shared/attendance';
import { ErrorCode } from 'constants/shared/ErrorCode';
import MyAlertStore from 'Alert/MyAlertStore';
import SuccessAlertStoreOfStudent from './Form/SuccessAlertStoreOfStudent';
import SoundStore from './Form/SoundStore';
import Config from 'config/Config';
import { SimpleTeacherT } from 'types/shared/core/Teacher';
import SuccessAlertStoreOfTeacher from './Form/SuccessAlertStoreOfTeacher';
import interfaceBridge from 'utils/interfaceBridge';
import SseStore from './SseStore';
import { AcademyT } from 'types/shared/core/Academy';
import {
  SseAttendanceNewStatusPayloadT,
  SseAttendancePayloadT,
} from 'types/shared/core/Attendance';
import DateHelper from 'utils/shared/DateHelper';
import SpeakStore from './SpeakStore';

const possibleKeys = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '*',
  '#',
  'Backspace',
  'Enter', // 등원
  'Add', // 하원
  '=', // 하원
];

export type AttLogT = {
  name: string;
  isTeacher: boolean;
  status: 'present' | 'left' | 'clockIn' | 'clockOut';
  createdAt: Date;
};

export default class AttendanceStore {
  myAlertStore = new MyAlertStore();
  successAlertStoreOfStudent = new SuccessAlertStoreOfStudent();
  successAlertStoreOfTeacher = new SuccessAlertStoreOfTeacher();

  soundStore = new SoundStore();
  speakStore = new SpeakStore();

  isWideTablet = false;
  isTeacherAtt = false;

  viewMode: 'form' | 'log' | 'queueDisplay' = 'form';

  chars: string[] = [];
  canTypeMore = true;

  possibleTargetStudentList: SimpleStudentT[] = [];
  possibleTargetTeacherList: SimpleTeacherT[] = [];

  dialogOpen = false;
  isStatusBtnEnabled = false;

  lastClickedStatus: 'present' | 'left' | 'clockIn' | 'clockOut' | null = null;

  logs: AttLogT[] = [];

  // 실시간 등하원 호출 관련
  academyInfo: AcademyT | null = null;
  isAttRealtimeEnabled = false;
  sseStore: SseStore;
  payloadQueue: SseAttendanceNewStatusPayloadT[] = [];

  constructor() {
    const academyInfoJsonStr = interfaceBridge.getData('ACADEMY_INFO');
    try {
      this.academyInfo = JSON.parse(academyInfoJsonStr);
    } catch (e) {
      this.academyInfo = null;
    }

    this.speakStore.bindAlertStore(this.myAlertStore);

    this.sseStore = new SseStore(
      this.academyInfo?.id || 0,
      'attendance',
      this.handleAttendanceEvent,
    );

    this.isAttRealtimeEnabled = this.academyInfo?.insiderSetting?.feaAttendanceRealtime || false;

    makeAutoObservable(this);
  }

  init = () => {
    console.log('isAttRealtimeEnabled', this.isAttRealtimeEnabled);
    if (!this.academyInfo) {
      // 학교정보가 훼손되면 로그인으로 보내버림
      interfaceBridge.goToSignIn();
    }

    if (this.isAttRealtimeEnabled) {
      this.sseStore.init();
      // const voices = window.speechSynthesis.getVoices();
    }
  };

  setIsWideTablet = (value: boolean) => {
    this.isWideTablet = value;
    console.log('isWideTablet', this.isWideTablet);
  };

  setViewMode = (mode: 'form' | 'log' | 'queueDisplay') => {
    console.log('setViewMode', mode);
    this.viewMode = mode;
  };

  handleStudentClick = (student: SimpleStudentT) => {
    this.dialogOpen = false;
    if (this.lastClickedStatus !== 'present' && this.lastClickedStatus !== 'left') {
      return;
    }

    this.createAttRecordFromDevice(this.lastClickedStatus, student);
  };

  handleTeacherClick = (teacher: SimpleTeacherT) => {
    this.dialogOpen = false;
    if (this.lastClickedStatus !== 'clockIn' && this.lastClickedStatus !== 'clockOut') {
      return;
    }

    this.createAttRecordOfTeacherFromDevice(this.lastClickedStatus, teacher);
  };

  handleDialogClose = () => {
    this.dialogOpen = false;
  };

  handleStatusBtnClick = (status: 'in' | 'out') => {
    this.soundStore.playSound('touch');

    if (this.isTeacherAtt) {
      // 교사
      this.createAttRecordOfTeacherFromDevice(status === 'in' ? 'clockIn' : 'clockOut');
    } else {
      // 학생
      this.createAttRecordFromDevice(status === 'in' ? 'present' : 'left');
    }
  };

  handleBackPressed = () => {
    this.deleteNumberField();
    this.soundStore.playSound('delete');
    // this.playDummySound();
  };

  deleteNumberField = () => {
    this.chars = [];
    this.updateButtonStatus();
  };

  handleKeyDown = (key: string) => {
    if (!possibleKeys.includes(key)) return;
    console.log(key);

    if (key === 'Backspace') {
      this.handleBackPressed();
    } else if (key === 'Enter') {
      if (this.isStatusBtnEnabled) {
        this.handleStatusBtnClick('in');
      }
    } else if (key === 'Add' || key === '=') {
      if (this.isStatusBtnEnabled) {
        this.handleStatusBtnClick('out');
      }
    } else {
      this.handleNumberPadClick(key);
    }
  };

  handleNumberPadClick = (char: string) => {
    if (!this.canTypeMore) return;
    this.chars.push(char);
    this.soundStore.playSound('touch');
    this.updateButtonStatus();
  };

  updateButtonStatus = () => {
    // 교사 모드인지
    this.isTeacherAtt = this.chars.length > 0 && this.chars[0] === '#';

    if (this.isInputDone()) {
      this.canTypeMore = false;
      this.isStatusBtnEnabled = true;
    } else {
      this.canTypeMore = true;
      this.isStatusBtnEnabled = false;
    }
  };

  createAttRecordFromDevice = async (status: 'present' | 'left', student?: SimpleStudentT) => {
    console.log('createAttRecordFromDevice start');

    const attCode = this.chars.join('');

    try {
      const result = await api<AttRecordCreateByCodeRequestT, MyResponse<SimpleStudentT>>(
        CREATE_ATT_RECORD_FROM_DEVICE(0),
        {
          academyId: 0,
          attCode,
          status,
          studentId: student?.id,
        },
      );

      console.log('createAttRecordFromDevice end');
      console.log('result', result);

      if (result.isSuccess && result.objects) {
        // objects 결과값이 1개인 것과 여러개인 것 의미 다름
        if (result.objects.length === 1) {
          this.handleStudentSuccess(result.objects[0], status);
        } else {
          this.handleAmbiguousCodeForStudent(result.objects, status);
        }
      } else {
        if (result.errorCode === ErrorCode.COMMON_EXPECTED_DATA_NOT_FOUND) {
          this.handleNoStudent();
        }
      }
    } catch (e) {
      console.log(e);
      await this.myAlertStore.customAlert(
        '오류',
        `문제가 발생했습니다. 문제가 계속되면 메뉴버튼을 눌러 로그아웃 후 다시 로그인 부탁드립니다.`,
        '닫기',
      );
    }
  };

  createAttRecordOfTeacherFromDevice = async (
    status: 'clockIn' | 'clockOut',
    teacher?: SimpleTeacherT,
  ) => {
    console.log('createAttRecordFromDevice start');

    const attCode = this.chars.join('');

    try {
      const result = await api<AttRecordOfTeacherCreateByCodeRequestT, MyResponse<SimpleTeacherT>>(
        CREATE_ATT_RECORD_OF_TEACHER_FROM_DEVICE(0),
        {
          academyId: 0,
          attCode,
          status,
          teacherId: teacher?.teacherId,
        },
      );

      console.log('createAttRecordFromDevice end');
      console.log('result', result);

      if (result.isSuccess && result.objects) {
        // objects 결과값이 1개인 것과 여러개인 것 의미 다름
        if (result.objects.length === 1) {
          this.handleTeacherSuccess(result.objects[0], status);
        } else {
          this.handleAmbiguousCodeForTeacher(result.objects, status);
        }
      } else {
        if (result.errorCode === ErrorCode.COMMON_EXPECTED_DATA_NOT_FOUND) {
          this.handleNoTeacher();
        }
      }
    } catch (e) {
      console.log(e);
      await this.myAlertStore.customAlert(
        '오류',
        `문제가 발생했습니다. 문제가 계속되면 메뉴버튼을 눌러 로그아웃 후 다시 로그인 부탁드립니다.`,
        '닫기',
      );
    }
  };

  handleStudentSuccess = async (student: SimpleStudentT, status: 'present' | 'left') => {
    console.log(student);
    console.log('handleSuccess', status);
    this.successAlertStoreOfStudent.show(student, status);
    this.soundStore.playSound('success');
    this.deleteNumberField();
    this.addLogOfStudent(student, status);
  };

  handleTeacherSuccess = async (teacher: SimpleTeacherT, status: 'clockIn' | 'clockOut') => {
    console.log(teacher);
    console.log('handleSuccess', status);
    this.successAlertStoreOfTeacher.show(teacher, status);
    this.soundStore.playSound('success');
    this.deleteNumberField();
    this.addLogOfTeacher(teacher, status);
  };

  addLogOfStudent = async (student: SimpleStudentT, status: 'present' | 'left') => {
    const date = new Date();
    const maskedName =
      student.name.length < 3
        ? student.name.substring(0, 1) + '*'
        : student.name.substring(0, 1) + '*' + student.name.substring(student.name.length - 1);

    const log = {
      name: maskedName,
      isTeacher: false,
      status,
      createdAt: date,
    };

    this.logs.unshift(log);
    if (this.logs.length > Config.maxLogCount) this.logs.pop();
  };

  addLogOfTeacher = async (teacher: SimpleTeacherT, status: 'clockIn' | 'clockOut') => {
    const date = new Date();
    const maskedName =
      teacher.nickname.length < 3
        ? teacher.nickname.substring(0, 1) + '*'
        : teacher.nickname.substring(0, 1) +
          '*' +
          teacher.nickname.substring(teacher.nickname.length - 1);

    const log = {
      name: maskedName,
      isTeacher: true,
      status,
      createdAt: date,
    };

    this.logs.unshift(log);
    if (this.logs.length > Config.maxLogCount) this.logs.pop();
  };

  handleAmbiguousCodeForStudent = (studentList: SimpleStudentT[], status: 'present' | 'left') => {
    this.possibleTargetStudentList = studentList;
    this.possibleTargetTeacherList = [];
    this.lastClickedStatus = status;
    this.dialogOpen = true;
  };

  handleAmbiguousCodeForTeacher = (
    teacherList: SimpleTeacherT[],
    status: 'clockIn' | 'clockOut',
  ) => {
    this.possibleTargetTeacherList = teacherList;
    this.possibleTargetStudentList = [];
    this.lastClickedStatus = status;
    this.dialogOpen = true;
  };

  handleNoStudent = async () => {
    this.soundStore.playSound('error');
    await this.myAlertStore.customAlert('오류', `코드에 해당하는 학생이 없습니다.`, '닫기');
    this.deleteNumberField();
  };

  handleNoTeacher = async () => {
    this.soundStore.playSound('error');
    await this.myAlertStore.customAlert('오류', `코드에 해당하는 교사가 없습니다.`, '닫기');
    this.deleteNumberField();
  };

  isInputDone = (): boolean => {
    if (!this.chars.length) return false;
    if (this.chars[0] === '*') {
      return this.chars.length >= 5;
    }
    if (this.chars[0] === '#') {
      return this.chars.length >= 5;
    }

    return this.chars.length >= 4;
  };

  welcome = async () => {
    await this.myAlertStore.customAlert(
      '어나더클래스 출결앱',
      `환영합니다. 코드를 입력해 등원/하원을 기록해주세요.`,
      '닫기',
      async () => {
        // 실패해도 조용히 넘김 (안내/alert는 testSound 버튼에서)
        await this.speakStore.warmup();
      },
    );
    this.deleteNumberField();
  };

  // realtime 관련

  handleAttendanceEvent = (ev: MessageEvent) => {
    const jsonPayload = ev.data;
    console.log('event arrieved', ev);

    try {
      const payload: SseAttendancePayloadT = JSON.parse(jsonPayload, DateHelper.dateReviver);
      console.log('payload', payload);
      if (payload.payloadType === 'newStatus') {
        this.handleNewStatusEvent(payload as SseAttendanceNewStatusPayloadT);
      }
    } catch (e) {
      // json parse error
    }
  };

  handleNewStatusEvent = async (payload: SseAttendanceNewStatusPayloadT) => {
    if (payload.newStatus !== 'called') {
      this.payloadQueue = this.payloadQueue.filter((p) => p.student.id !== payload.student.id);
      return;
    }

    console.log('handleNewStatusEven');

    this.payloadQueue = [
      payload,
      ...this.payloadQueue.filter((p) => p.student.id !== payload.student.id),
    ];

    await this.playSound(payload);
  };

  playSound = async (payload: SseAttendanceNewStatusPayloadT) => {
    const studentName = payload.student.name;
    const teacherName = payload.teacher?.nickname || '선생님';
    const classroom = payload.classroom || '';

    const speechText = classroom
      ? `${studentName} 학생, ${classroom}, ${teacherName}에게로 와주세요.`
      : `${studentName} 학생, ${teacherName}에게로 와주세요.`;

    await this.speakStore.speak(speechText);
  };
}
